/**
 * ProcurementQualityPriceDialog — Quality Price vs Basis
 * 1:1 port của Access `frmProcurementMainSubFormQualityPrice`: header (Date + Refresh)
 * + subform datasheet đọc view `v_changecost_quality` lọc theo 1 ngày.
 * Read-only (Access không có CRUD/print).
 *
 * Grid theo template `/convert-table` (dataList): FilterBar 1-nút Search global +
 * per-column filter ▽ + CSS `app-dataframe`/`app-table-header-cell` + formatter Access-parity.
 */
import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { TableVirtuoso } from 'react-virtuoso'

import DateTextField from '../../components/DateTextField'
import { FILTER_HEADER_INPUT_SX } from '../../theme/responsive'
import { useListQualityPriceQuery, type QualityPriceItem } from '../../store/api/qualityPriceApi'
import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  type ColumnFilterAnchorPosition,
} from '../../components/dataList'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'

interface Props {
  open: boolean
  onClose: () => void
}

// Style nút trong DialogTitle — đồng bộ các form dialog khác (IssueFormDialog...).
const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

type ColumnKind = 'date' | 'number' | 'text'
type ColumnDef = {
  key: keyof QualityPriceItem
  label: string
  kind?: ColumnKind
  align?: 'right'
}

// Thứ tự + label khớp datasheet Access `frmProcurementQryQualityPrice`
// (không có ColumnOrder → theo thứ tự control; Caption = label verbatim).
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'dateActual', label: 'Date Actual', kind: 'date' },
  { key: 'code', label: 'Quality Code' },
  { key: 'qualityPremiumRaw', label: 'Premium vs Basis', kind: 'number', align: 'right' },
  { key: 'priceUnit', label: 'Premium Unit' },
  { key: 'fx', label: 'FX', kind: 'number', align: 'right' },
  { key: 'price', label: 'Basis Price (VNĐ/KG)', kind: 'number', align: 'right' },
  { key: 'priceFromG2', label: 'Price vs Basis (VNĐ/KG)', kind: 'number', align: 'right' },
  { key: 'maxDateUpdates', label: 'Last Updated Date', kind: 'date' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => {
  if (!v) return ''
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}
const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const cellValue = (row: QualityPriceItem, col: ColumnDef): string => {
  const v = row[col.key]
  if (v == null || v === '') return ''
  if (col.kind === 'date') return formatAccessDate(v)
  if (col.kind === 'number' && typeof v === 'number') return formatAccessNumber(v)
  return String(v)
}

export default function ProcurementQualityPriceDialog({ open, onClose }: Props) {
  const [asOfDate, setAsOfDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof QualityPriceItem> | null>(null)
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof QualityPriceItem
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const { data, isFetching, error } = useListQualityPriceQuery(
    { date: asOfDate },
    { skip: !open || !asOfDate },
  )

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [baseRows, searchTerm, columnFilters.filters],
  )
  const rows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    filterPopover.close()
  }

  const popoverColumn = useMemo(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const toggleSort = (key: keyof QualityPriceItem) => setSortState((c) => toggleTableSort(c, key))
  const gridKey = `${asOfDate}:${filteredRows.length}`

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" component="div" fontWeight={700}>
          Quality Price vs Basis
        </Typography>
        {/* btnCancel Access ở FormHeader top-right (KHÔNG DialogActions bottom) — theo /convert-form. */}
        <Button type="button" variant="outlined" size="small" onClick={onClose} sx={{ ...titleBtnSx, ml: 'auto' }}>
          Cancel
        </Button>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1.5, display: 'flex', flexDirection: 'column', height: '72vh' }}>
        <FilterBar
          hasActiveFilters={hasActiveFilters}
          onClearAll={clearAllFilters}
          activeFilterCount={activeFilterCount}
          rightSlot={
            <DateTextField
              size="small"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={asOfDate}
              onChange={setAsOfDate}
              sx={FILTER_HEADER_INPUT_SX}
              nativeInputProps={{ 'aria-label': 'Quality Price date' }}
            />
          }
        >
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
        </FilterBar>

        <Divider sx={{ mb: 0.5 }} />

        {error ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            Failed to load data.
          </Alert>
        ) : null}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            // Trong Dialog cha đã bounded (DialogContent height cố định) → bảng fill phần còn lại.
            '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
          }}
        >
          <Box className="app-dataframe">
            <Box
              className="app-dataframe-toolbar"
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, minHeight: 30 }}
            >
              {isFetching ? <CircularProgress size={15} /> : null}
              <Typography variant="caption" color="text.secondary">
                Total: {filteredRows.length}
              </Typography>
            </Box>

            <FilterPopover
              open={filterPopover.state !== null}
              anchorEl={filterPopover.state?.anchor ?? null}
              onClose={() => filterPopover.close()}
            >
              {filterPopover.isOpen('search') && (
                <SearchFilterContent
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Code / Unit..."
                  onClear={() => filterPopover.close()}
                />
              )}
            </FilterPopover>

            <ColumnFilterPopover
              open={colPopover !== null}
              anchorPosition={colPopover?.position ?? null}
              onClose={() => setColPopover(null)}
              label={popoverColumn?.label ?? ''}
              kind={popoverKind === 'none' ? 'text' : popoverKind}
              value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
              onChange={(v) => {
                if (!colPopover) return
                columnFilters.setFilter(String(colPopover.key), v)
              }}
              options={popoverOptions}
            />

            <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
              {rows.length === 0 && !isFetching ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    No data for the selected date.
                  </Typography>
                </Box>
              ) : (
                <TableVirtuoso
                  key={gridKey}
                  style={{ height: '100%' }}
                  data={rows}
                  fixedHeaderContent={() => (
                    <TableRow>
                      {TABLE_COLUMNS.map((col) => (
                        <TableCell
                          key={String(col.key)}
                          align={col.align}
                          className="app-table-header-cell"
                          onClick={() => toggleSort(col.key)}
                          sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', position: 'relative' }}
                        >
                          {col.label}
                          {getTableSortSuffix(sortState, col.key)}
                          <ColumnFilterIcon
                            active={columnFilters.isActive(String(col.key))}
                            onClick={(position) => setColPopover({ key: col.key, position })}
                            ariaLabel={`Filter ${col.label}`}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                  itemContent={(_, row: QualityPriceItem) =>
                    TABLE_COLUMNS.map((col) => {
                      const val = cellValue(row, col)
                      return (
                        <TableCell
                          key={String(col.key)}
                          align={col.align}
                          sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          <Box component="span" className="app-table-cell-text" title={val || '-'}>
                            {val || '-'}
                          </Box>
                        </TableCell>
                      )
                    })
                  }
                  components={{
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 780, width: '100%' }} />,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    TableHead: ((props: any) => <TableHead {...props} component="thead" />) as never,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as never,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    TableRow: (props: any) => <TableRow {...props} hover />,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
